import { cn } from "@theprelude/core";
import { Truncate } from "@theprelude/ds";
import { Position } from "@xyflow/react";
import { HTMLAttributes, ReactNode, useRef } from "react";
import { tn } from "@/lib/tracking";
import { ConnectionIndicator } from "../connection-indicator";
import { useControlMonitorNavigation } from "../control-monitor-navigation";

export interface NodeBaseProps extends HTMLAttributes<HTMLDivElement> {
  nodeId: string;
  name: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  children: ReactNode;
  actionsDropdown?: ReactNode;
  hasParent?: boolean;
  hasChildren?: boolean;
  headerClassName?: string;
}

const PREVENT_CLICK_DRAG_DELTA = 6;

export const NodeBase = ({
  nodeId,
  name,
  description,
  icon,
  disabled = false,
  children,
  actionsDropdown,
  hasParent = false,
  hasChildren = false,
  className,
  headerClassName,
  onClick,
  ...props
}: NodeBaseProps) => {
  const mouseDownPosition = useRef<[number, number]>([0, 0]);

  const { path } = useControlMonitorNavigation();
  const isExpanded = path.includes(nodeId);

  return (
    <div className="h-full w-full">
      <div
        data-type="react-flow-node"
        className={cn(
          "flex h-full w-full flex-col justify-between overflow-hidden rounded-sm border border-stroke-line bg-surface-background transition-colors duration-300 dark:border-stroke-line",
          disabled
            ? "cursor-not-allowed text-tertiary-foreground dark:text-stroke-line"
            : "cursor-pointer text-secondary-foreground",
          className,
          tn(name),
        )}
        onMouseDown={(e) => {
          mouseDownPosition.current = [e.pageX, e.pageY];
        }}
        onClick={(e) => {
          if (disabled) return;
          const diffX = Math.abs(e.pageX - mouseDownPosition.current[0]);
          const diffY = Math.abs(e.pageY - mouseDownPosition.current[1]);
          if (
            diffX < PREVENT_CLICK_DRAG_DELTA &&
            diffY < PREVENT_CLICK_DRAG_DELTA
          ) {
            onClick?.(e);
          }
        }}
        {...props}
      >
        <div
          className={cn(
            "group flex w-full flex-1 items-center gap-2 px-2 text-primary-foreground font-semibold",
            isExpanded &&
              !disabled &&
              "expanded bg-secondary-foreground text-white dark:bg-tertiary-background",
            disabled &&
              "text-tertiary-foreground transition-colors duration-300 dark:text-stroke-line",
            headerClassName,
          )}
        >
          <span
            className={cn(
              isExpanded ? "text-white" : "text-secondary-foreground",
              disabled && "text-tertiary-foreground",
            )}
          >
            {icon}
          </span>
          <div className="flex flex-1 flex-col">
            <span className="max-w-full leading-tight">
              <Truncate text={name}>{name}</Truncate>
            </span>
            {description && (
              <span
                className={cn(
                  "max-w-full text-xs leading-tight text-secondary-foreground font-medium",
                  isExpanded && !disabled && "text-white",
                  disabled &&
                    "text-tertiary-foreground transition-colors duration-300 dark:text-stroke-line",
                )}
              >
                <Truncate text={description}>{description}</Truncate>
              </span>
            )}
          </div>
          {actionsDropdown}
        </div>
        {children}
      </div>
      {hasParent && (
        <ConnectionIndicator
          type="target"
          position={Position.Left}
          isConnected={isExpanded}
          disabled={disabled}
        />
      )}
      {hasChildren && (
        <ConnectionIndicator
          type="source"
          position={Position.Right}
          isConnected={isExpanded}
          disabled={disabled}
        />
      )}
    </div>
  );
};
